import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import Feather from '@react-native-vector-icons/feather/static';
import { GTFonts, useTheme } from '../../theme';
import { fs, iconSize, s, v } from '../../utils/libs';

interface PickerPageHeaderProps {
  title: string;
  subtitle?: string;
  /** Shows ← before the title: a page opened from the list. */
  onBack?: () => void;
  /** Shows the round × on the right. */
  onClose?: () => void;
  /** Shows a round settings button before the ×. */
  onSettings?: () => void;
  /** Tighter spacing inside another sheet's content. */
  compact?: boolean;
}

/** Title block of a picker page: optional ←, title, one line under it. */
const PickerPageHeader: React.FC<PickerPageHeaderProps> = ({
  title,
  subtitle,
  onBack,
  onClose,
  onSettings,
  compact = false,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  return (
    <View style={[styles.header, compact && styles.headerCompact]}>
      {onBack ? (
        <Pressable
          onPress={onBack}
          accessibilityRole="button"
          accessibilityLabel="Back"
          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
          style={({ pressed }) => [styles.back, pressed && styles.pressed]}>
          <Feather
            name="arrow-left"
            size={iconSize(22)}
            color={theme.colors.text}
          />
        </Pressable>
      ) : null}
      <View style={styles.titles}>
        <Text
          style={[styles.title, compact && styles.titleCompact]}
          accessibilityRole="header"
          numberOfLines={1}>
          {title}
        </Text>
        {subtitle ? (
          <Text style={styles.subtitle} numberOfLines={2}>
            {subtitle}
          </Text>
        ) : null}
      </View>
      {onSettings || onClose ? (
        <View style={styles.actions}>
          {onSettings ? (
            <Pressable
              onPress={onSettings}
              accessibilityRole="button"
              accessibilityLabel="Settings"
              style={({ pressed }) => [
                styles.close,
                pressed && styles.pressed,
              ]}>
              <Feather
                name="settings"
                size={iconSize(18)}
                color={theme.colors.text}
              />
            </Pressable>
          ) : null}
          {onClose ? (
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Close"
              style={({ pressed }) => [
                styles.close,
                pressed && styles.pressed,
              ]}>
              <Feather name="x" size={iconSize(18)} color={theme.colors.text} />
            </Pressable>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

const pointer = Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {};

const createStyles = (theme: any) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: s(6),
      paddingTop: v(10),
      paddingBottom: v(14),
      paddingLeft: s(20),
      paddingRight: s(12),
    },
    headerCompact: {
      paddingTop: v(4),
      paddingBottom: v(10),
      paddingLeft: s(4),
      paddingRight: s(4),
    },
    back: {
      width: s(40),
      height: s(40),
      marginLeft: -s(10),
      marginTop: -v(6),
      borderRadius: s(20),
      alignItems: 'center',
      justifyContent: 'center',
      ...pointer,
    },
    titles: {
      flex: 1,
      minWidth: 0,
      gap: v(3),
    },
    title: {
      fontFamily: GTFonts.bold,
      fontSize: fs(21),
      lineHeight: fs(26),
      letterSpacing: -0.3,
      color: theme.colors.text,
    },
    titleCompact: {
      fontSize: fs(17),
      lineHeight: fs(22),
    },
    subtitle: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textGray500,
    },
    actions: {
      flexDirection: 'row',
      gap: s(8),
      marginTop: -v(4),
    },
    close: {
      width: s(40),
      height: s(40),
      borderRadius: s(20),
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.surfaceSecondary,
      ...pointer,
    },
    pressed: {
      opacity: 0.7,
    },
  });

export default PickerPageHeader;
